import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import PublicLayout from '../../components/layouts/PublicLayout'
import { apiClient, getErrorMessage } from '../../api/client'
import {
  AlertTriangle,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
} from 'lucide-react'

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
]

const contactSchema = z.object({
  organization_name: z.string().min(2, 'Organization name is required'),
  contact_name: z.string().min(2, 'Contact name is required'),
  business_email: z.string().email('Valid email is required'),
  phone: z.string().min(10, 'Valid phone number is required'),
  states_of_operation: z.array(z.string()).min(1, 'Select at least one state'),
  number_of_clients: z.enum(['1-25', '26-100', '101-500', '500+']),
  current_ehr: z.string().optional(),
  primary_interest: z.array(z.string()).min(1, 'Select at least one interest'),
  notes: z.string().optional(),
})

type ContactFormData = z.infer<typeof contactSchema>

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      states_of_operation: [],
      primary_interest: [],
    },
  })

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      await apiClient.post('/api/v1/public/contact/lead/', data)
      setSubmitSuccess(true)
      reset()
    } catch (error) {
      setSubmitError(getErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    return (
      <PublicLayout>
        <div className="bg-white py-24">
          <div className="mx-auto max-w-2xl px-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900">Thank You!</h1>
            <p className="mt-4 text-lg text-gray-600">
              We've received your request and will contact you within 1 business day
              to schedule your demo.
            </p>
            <button
              onClick={() => setSubmitSuccess(false)}
              className="mt-8 btn-primary"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </PublicLayout>
    )
  }

  return (
    <PublicLayout>
      <div className="bg-gradient-to-b from-primary-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Request a Demo
            </h1>
            <p className="mt-6 text-lg text-gray-600">
              See how Grace Alone ABA can protect your practice with audit-ready
              documentation and billing integrity.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              {/* PHI Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">
                      Do Not Enter Protected Health Information
                    </p>
                    <p className="mt-1 text-sm text-yellow-700">
                      This form is for business inquiries only. Do NOT include any
                      client names, dates of birth, diagnoses, insurance information,
                      or any other PHI.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800">{submitError}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="label">Organization Name *</label>
                    <input
                      type="text"
                      {...register('organization_name')}
                      className="mt-1 input"
                      placeholder="Your ABA Practice Name"
                    />
                    {errors.organization_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.organization_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Contact Name *</label>
                    <input
                      type="text"
                      {...register('contact_name')}
                      className="mt-1 input"
                      placeholder="Your Name"
                    />
                    {errors.contact_name && (
                      <p className="mt-1 text-sm text-red-600">{errors.contact_name.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="label">Business Email *</label>
                    <input
                      type="email"
                      {...register('business_email')}
                      className="mt-1 input"
                      placeholder="you@yourpractice.com"
                    />
                    {errors.business_email && (
                      <p className="mt-1 text-sm text-red-600">{errors.business_email.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Business email required (no personal emails)
                    </p>
                  </div>

                  <div>
                    <label className="label">Phone *</label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className="mt-1 input"
                      placeholder="(555) 555-5555"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">States of Operation *</label>
                  <div className="mt-2 grid grid-cols-5 sm:grid-cols-10 gap-2">
                    {US_STATES.map((state) => (
                      <label key={state} className="flex items-center">
                        <input
                          type="checkbox"
                          value={state}
                          {...register('states_of_operation')}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-1 text-sm text-gray-700">{state}</span>
                      </label>
                    ))}
                  </div>
                  {errors.states_of_operation && (
                    <p className="mt-1 text-sm text-red-600">{errors.states_of_operation.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="label">Number of Active Clients *</label>
                    <select {...register('number_of_clients')} className="mt-1 input">
                      <option value="">Select...</option>
                      <option value="1-25">1-25</option>
                      <option value="26-100">26-100</option>
                      <option value="101-500">101-500</option>
                      <option value="500+">500+</option>
                    </select>
                    {errors.number_of_clients && (
                      <p className="mt-1 text-sm text-red-600">{errors.number_of_clients.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Current EHR (if any)</label>
                    <input
                      type="text"
                      {...register('current_ehr')}
                      className="mt-1 input"
                      placeholder="e.g., CentralReach, Catalyst"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Primary Interest *</label>
                  <div className="mt-2 space-y-2">
                    {['clinical', 'billing', 'both'].map((interest) => (
                      <label key={interest} className="flex items-center">
                        <input
                          type="checkbox"
                          value={interest}
                          {...register('primary_interest')}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700 capitalize">
                          {interest === 'both' ? 'Both Clinical & Billing' : `${interest} Documentation`}
                        </span>
                      </label>
                    ))}
                  </div>
                  {errors.primary_interest && (
                    <p className="mt-1 text-sm text-red-600">{errors.primary_interest.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Additional Notes</label>
                  <textarea
                    {...register('notes')}
                    rows={4}
                    className="mt-1 input"
                    placeholder="Any specific questions or requirements (NO PHI)"
                  />
                  {errors.notes && (
                    <p className="mt-1 text-sm text-red-600">{errors.notes.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full py-3"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Demo'}
                </button>
              </form>
            </div>

            {/* Contact Info Sidebar */}
            <div>
              <div className="bg-gray-50 rounded-lg p-6 sticky top-24">
                <h3 className="font-semibold text-gray-900 mb-6">Contact Information</h3>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-primary-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Email</p>
                      <p className="text-sm text-gray-600">info@gracealoneaba.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-primary-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-gray-900">Phone</p>
                      <p className="text-sm text-gray-600">(973) 506-9096</p>
                    </div>
                  </div>
                </div>

                <hr className="my-6" />

                <div>
                  <p className="font-medium text-gray-900 mb-2">What to Expect</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                      Response within 1 business day
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                      30-minute personalized demo
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                      Pricing based on your needs
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                      No pressure, no obligation
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
